import React, { useContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield, LayoutDashboard, Users, Send, Settings as SettingsIcon, LogOut, Bell, Search, CreditCard, ShieldCheck } from 'lucide-react';
import { AuthContext } from '../App';
import { Input } from '../components/ui/input';

const DashboardLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { to: '/app', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/app/clients', icon: Users, label: 'Clients' },
    { to: '/app/disputes', icon: Send, label: 'Disputes' },
    { to: '/app/billing', icon: CreditCard, label: 'Billing' },
    { to: '/app/settings', icon: SettingsIcon, label: 'Settings' },
  ];

  const adminNavItem = { to: '/app/admin', icon: ShieldCheck, label: 'Admin' };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.to;
    return location.pathname.startsWith(item.to);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex bg-[#f7f6f0] text-stone-800">
      {/* Sidebar */}
      <aside className="w-64 border-r border-stone-200/80 bg-[#ffffff] flex flex-col">
        <div className="h-16 flex items-center px-6 border-b border-stone-200/80">
          <Link to="/app" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-sm tracking-wider">
              INFANTRY <span className="text-[#4b5320]">/ DISPUTE</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-6 px-3">
          <div className="text-xs uppercase tracking-widest text-stone-400 px-3 mb-3">Operations</div>
          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    active
                      ? 'bg-[#4b5320]/10 text-[#4b5320] border-l-2 border-[#4b5320]'
                      : 'text-stone-700 hover:text-stone-900 hover:bg-stone-100/60 border-l-2 border-transparent'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                  {item.label}
                </Link>
              );
            })}
          </div>

          {user?.isAdmin && (
            <>
              <div className="text-xs uppercase tracking-widest text-stone-400 px-3 mt-6 mb-3">Owner</div>
              <Link
                to={adminNavItem.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive(adminNavItem)
                    ? 'bg-[#4b5320]/10 text-[#4b5320] border-l-2 border-[#4b5320]'
                    : 'text-stone-700 hover:text-stone-900 hover:bg-stone-100/60 border-l-2 border-transparent'
                }`}
              >
                <ShieldCheck className="w-4 h-4" strokeWidth={1.8} />
                {adminNavItem.label}
              </Link>
            </>
          )}
        </nav>

        <div className="border-t border-stone-200/80 p-4">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 bg-[#4b5320]/20 border border-[#4b5320]/40 text-[#4b5320] flex items-center justify-center rounded-sm font-semibold text-sm">
              {user?.avatar || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-stone-800 truncate">{user?.name}</div>
              <div className="text-xs text-stone-500 truncate">{user?.company}</div>
            </div>
            <button onClick={handleLogout} className="text-stone-500 hover:text-stone-800 transition-colors" title="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-stone-200/80 bg-[#f7f6f0]/90 backdrop-blur flex items-center px-8 gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
            <Input placeholder="Search clients, disputes..." className="pl-10 bg-[#fafaf6] border-stone-200 rounded-sm h-9 text-sm" />
          </div>
          <div className="flex items-center gap-4">
            <button className="text-stone-700 hover:text-stone-900 relative">
              <Bell className="w-5 h-5" strokeWidth={1.8} />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#4b5320] rounded-full"></span>
            </button>
            <div className="text-xs uppercase tracking-widest text-stone-500 border border-stone-200 px-2.5 py-1 rounded-sm">
              {user?.plan || 'Pro Dispute'}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
